import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { BookOpen, Video, CheckCircle, Clock, Target, Users, ArrowRight } from 'lucide-react';

const TradersExamStudyGuide = () => {
  const studyMaterials = {
    book: {
      title: "Printed Traders Exam Study Guide",
      subtitle: "Complete Study Book",
      description: "Comprehensive exam-focused study guide covering all 10 CIRO syllabus elements for the Traders Licensing Examination.",
      price: "299.99",
      link: "https://a.co/d/0c400Tnw"
    },
    course: {
      title: "Traders Exam Video Course",
      price: "199",
      status: "Coming Soon",
      features: ["Online Book", "Video Lectures", "Podcast", "Interactive Games", "Practice Questions", "Mock Exams"]
    }
  };

  const learningPath = [
    {
      phase: "Phase 1: Getting Started",
      description: "Understand the exam basics and create your study plan",
      duration: "Week 1",
      topics: [
        {
          title: "Understanding the Traders Exam Structure",
          description: "100 questions, 2 hours, 10 elements, proctored exam format"
        },
        {
          title: "Element 1: The Regulatory Environment",
          description: "CIRO rules and Canadian securities regulations (4 questions)"
        },
        {
          title: "Element 2: Capital Formation",
          description: "Primary and secondary markets, IPOs (4 questions)"
        }
      ]
    },
    {
      phase: "Phase 2: Trade Execution Fundamentals",
      description: "Master the core trading concepts",
      duration: "Week 1-2",
      topics: [
        {
          title: "Element 3: Role of Traders & Trade Execution",
          description: "Trader responsibilities and execution best practices (10 questions)"
        },
        {
          title: "Element 4: Marketplaces",
          description: "Exchange structures, market types, trading venues (14 questions)"
        }
      ]
    },
    {
      phase: "Phase 3: Trading Methods & Rules (High Weight)",
      description: "Focus on the highest-weighted exam sections (~36% of exam)",
      duration: "Week 2-3",
      topics: [
        {
          title: "Element 5: Methods of Trading",
          description: "Order types, algorithmic trading, execution strategies (16 questions)"
        },
        {
          title: "Element 6: Trading Rules",
          description: "CIRO trading rules, short selling, market integrity (20 questions - HIGHEST WEIGHT)"
        }
      ]
    },
    {
      phase: "Phase 4: Compliance & Operations",
      description: "Trade desk operations, derivatives, and settlement",
      duration: "Week 3-4",
      topics: [
        {
          title: "Element 7: Trade Desk Function & Compliance",
          description: "Supervision, compliance, trade desk operations (9 questions)"
        },
        {
          title: "Element 8: Derivatives Requirements",
          description: "Options, futures, derivative trading regulations (9 questions)"
        },
        {
          title: "Element 9: Clearing & Settlement",
          description: "Trade settlement, clearing processes, CDS (6 questions)"
        }
      ]
    },
    {
      phase: "Phase 5: Ethics & Final Review",
      description: "Complete ethics module and prepare for exam day",
      duration: "Week 4-5",
      topics: [
        {
          title: "Element 10: Ethics & Confidentiality",
          description: "Professional conduct, conflicts of interest (8 questions)"
        },
        {
          title: "Full Mock Exams",
          description: "Complete practice exams under timed conditions"
        },
        {
          title: "Final Review & Weak Areas",
          description: "Focus on challenging topics identified in practice"
        }
      ]
    }
  ];

  const examElements = [
    { num: 1, title: 'The Regulatory Environment', questions: 4, weight: '4%' },
    { num: 2, title: 'Capital Formation', questions: 4, weight: '4%' },
    { num: 3, title: 'Role of Traders & Trade Execution', questions: 10, weight: '10%' },
    { num: 4, title: 'Marketplaces', questions: 14, weight: '14%' },
    { num: 5, title: 'Methods of Trading', questions: 16, weight: '16%' },
    { num: 6, title: 'Trading Rules', questions: 20, weight: '20%' },
    { num: 7, title: 'Trade Desk Function & Compliance', questions: 9, weight: '9%' },
    { num: 8, title: 'Derivatives Requirements', questions: 9, weight: '9%' },
    { num: 9, title: 'Clearing & Settlement', questions: 6, weight: '6%' },
    { num: 10, title: 'Ethics & Confidentiality', questions: 8, weight: '8%' }
  ];

  return (
    <>
      <SEO 
        title="CIRO Traders Exam Study Guide | Free Learning Path & Resources | Vitta Finance Academy"
        description="Master the CIRO Traders Licensing Examination with our structured study guide. Free learning path covering trading rules, marketplaces, trade execution, and all 10 elements. Pass the Traders Exam on your first attempt."
        keywords="Traders Exam study guide, Traders Exam preparation, CIRO Traders learning path, how to study for Traders Exam, Traders Exam topics, Traders Exam free resources, Traders Licensing Examination guide, Traders Exam strategy"
      />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        padding: '120px 20px 80px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <span style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            FREE STUDY RESOURCE
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            CIRO Traders Exam Study Guide
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.7'
          }}>
            Your complete learning path to pass the Traders Licensing Examination. 
            Master all 10 elements from trading rules to market structure.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/traders-exam"
              data-testid="traders-course-btn"
              style={{
                background: '#d4af37',
                color: '#0d9488',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              View Traders Exam Course
              <ArrowRight size={18} />
            </Link>
            <a 
              href="https://a.co/d/0c400Tnw"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="traders-amazon-studyguide-btn"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <BookOpen size={18} />
              Get Study Book on Amazon
            </a>
          </div>
        </div>
      </section>

      {/* Exam Overview */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#0d9488', marginBottom: '40px' }}>
            Traders Exam at a Glance
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '20px'
          }}>
            {[
              { label: 'Questions', value: '100', sublabel: 'Multiple Choice' },
              { label: 'Duration', value: '2 Hours', sublabel: 'Proctored' },
              { label: 'Attempts', value: '3', sublabel: 'Per Exam' },
              { label: 'Elements', value: '10', sublabel: 'CIRO Topics' },
              { label: 'Highest Weight', value: 'Trading Rules', sublabel: '20 Questions' }
            ].map((stat, index) => (
              <div key={index} style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#0d9488' }}>{stat.value}</div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginTop: '5px' }}>{stat.label}</div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '3px' }}>{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Element Weightings */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#0d9488', marginBottom: '15px' }}>
            Exam Element Weightings
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '40px', maxWidth: '600px', margin: '0 auto 40px' }}>
            Focus your study time on high-weight elements for maximum impact
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {examElements.map((element) => (
              <div 
                key={element.num}
                style={{
                  background: element.questions >= 16 ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' : 'white',
                  color: element.questions >= 16 ? 'white' : '#374151',
                  padding: '20px',
                  borderRadius: '10px',
                  border: element.questions >= 16 ? 'none' : '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: element.questions >= 16 ? 'rgba(255,255,255,0.2)' : '#0d9488',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {element.num}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '2px' }}>
                    {element.title}
                  </div>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    opacity: element.questions >= 16 ? 0.9 : 0.7 
                  }}>
                    {element.questions} Questions ({element.weight})
                  </div>
                </div>
                {element.questions >= 16 && (
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    fontWeight: '700'
                  }}>
                    HIGH WEIGHT
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section style={{ padding: '80px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#0d9488', marginBottom: '15px' }}>
            Structured Learning Path
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '50px', maxWidth: '600px', margin: '0 auto 50px' }}>
            Follow this 5-phase study plan to systematically master all Traders Exam content
          </p>

          {learningPath.map((phase, phaseIndex) => (
            <div key={phaseIndex} style={{ marginBottom: '40px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '1.25rem',
                  flexShrink: 0
                }}>
                  {phaseIndex + 1}
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', color: '#0d9488', margin: 0 }}>{phase.phase}</h3>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '3px 0 0' }}>
                    {phase.description} - <span style={{ color: '#d4af37', fontWeight: '600' }}>{phase.duration}</span>
                  </p>
                </div>
              </div>

              <div style={{
                marginLeft: '25px',
                borderLeft: phaseIndex < learningPath.length - 1 ? '2px solid #e5e7eb' : 'none',
                paddingLeft: '40px',
                paddingBottom: phaseIndex < learningPath.length - 1 ? '20px' : '0'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '15px'
                }}>
                  {phase.topics.map((topic, topicIndex) => (
                    <div 
                      key={topicIndex}
                      style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <h4 style={{ 
                        fontSize: '1rem', 
                        color: '#0d9488', 
                        margin: '0 0 8px',
                        lineHeight: '1.4'
                      }}>
                        {topic.title}
                      </h4>
                      <p style={{ 
                        fontSize: '0.85rem', 
                        color: '#6b7280', 
                        margin: 0,
                        lineHeight: '1.5'
                      }}>
                        {topic.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Recommended Study Resources */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#0d9488', marginBottom: '30px' }}>
            Recommended Study Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '25px'
          }}>
            {/* Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #0d9488',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <BookOpen size={28} color="white" />
              </div>
              <div style={{
                display: 'inline-block',
                backgroundColor: '#ccfbf1',
                color: '#0d9488',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '0.7rem',
                fontWeight: '700',
                marginBottom: '12px'
              }}>
                AVAILABLE NOW
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#0d9488', marginBottom: '8px' }}>
                {studyMaterials.book.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.book.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0d9488' }}>
                  CAD ${studyMaterials.book.price}
                </span>
              </div>
              <a 
                href={studyMaterials.book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#0d9488',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Course */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '20px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8860b 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700'
              }}>COMING SOON</div>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8860b 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Video size={28} color="white" />
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#0d9488', marginBottom: '8px' }}>
                {studyMaterials.course.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                Complete video course with all study materials included.
              </p>
              <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                {studyMaterials.course.features.map((feature, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', justifyContent: 'center' }}>
                    <CheckCircle size={14} color="#0d9488" />
                    <span style={{ fontSize: '0.8rem', color: '#475569' }}>{feature}</span>
                  </div>
                ))}
              </div>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0d9488' }}>
                  CAD ${studyMaterials.course.price}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}> one-time</span>
              </div>
              <button 
                disabled
                style={{
                  display: 'inline-block',
                  background: '#e2e8f0',
                  color: '#64748b',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'not-allowed'
                }}
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '15px' }}>Ready to Start Your Traders Exam Journey?</h2>
          <p style={{ opacity: '0.9', marginBottom: '30px', fontSize: '1.1rem' }}>
            Get your comprehensive study guide and start preparing today.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="https://a.co/d/0c400Tnw"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-traders-amazon"
              style={{
                background: '#d4af37',
                color: '#0d9488',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <BookOpen size={20} />
              Get Study Guide - $299.99
            </a>
            <Link 
              to="/traders-exam"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              View Course Details
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default TradersExamStudyGuide;
